import { capitalizeFirstLetter, validateEmail } from '@getaccept/lib-shared-new/src/helpers';
import Analytics from '@getaccept/lib-shared-new/src/analytics';
import { AnalyticsEvent, AnalyticsUserTrait } from '@getaccept/lib-shared-new/src/types/analytics';
import bugsnagClient from '@getaccept/lib-shared-new/src/bugsnag';
import type { Language } from '@getaccept/lib-shared-new/src/languages/enums/language';
import { LocalStorageHelper } from '@getaccept/lib-shared-new/src/helpers/local-storage.helper';
import { LocalStorageKey } from '@getaccept/lib-shared-new/src/enums/local-storage';
import { gtag } from 'ga-gtag';
import type { SignupPayload } from '../types/signup-payload';
import type { SignupResponse } from '../types/signup-response';
import SignupService from '../services/signup.service';
import { Hubspot } from '../services/hubspot.service';
import type { ErrorMessage } from '../types/error-message';
import type { IdentityProvider } from '../signup/types/enums/identity-provider';
import { messageValidator } from '../external/constants/message-validator.constants';
import type { IdentityProviderResponseDTO } from '../external/types/identity-provider-response-dto';
import type { SignupQueryParams } from '../types/signup-query-params';
import { IdentityProviderHelper } from './identity-provider.helper';

const unknownError: ErrorMessage = {
  title: 'something-went-wrong',
  description: 'please-try-again-or-contact-support_v2',
};
export class SignupHelper {
  static containsNumbersOrSymbols(strings: string[]) {
    const numberRegex = /\d/;
    const symbolRegex = /[^a-zA-Z0-9]/g;

    return strings.some((string: string) => numberRegex.test(string) || symbolRegex.test(string));
  }

  static getNamesAndEntityFromEmail(email: string) {
    const unhandledEmailResponse = {
      firstName: '',
      lastName: '',
      entityName: '',
    };

    if (!validateEmail(email.toLowerCase())) {
      return unhandledEmailResponse;
    }

    const names: string[] = email
      .split('@')[0]
      .split('.')
      .map((name: string) => capitalizeFirstLetter(name));

    if (names.length === 1 || names[0].length === 1 || this.containsNumbersOrSymbols(names)) {
      return unhandledEmailResponse;
    }

    const lastNames: string[] = names.slice(1, names.length);

    if (lastNames.some((name: string) => name.length === 1)) {
      return unhandledEmailResponse;
    }

    const entity: string = capitalizeFirstLetter(email.split('@')[1].split('.')[0]);

    return {
      firstName: names[0],
      lastName: lastNames.join(' ').trim(),
      entityName: entity.trim(),
    };
  }
  static async signup(payload: SignupPayload): Promise<ErrorMessage | null> {
    try {
      const response: SignupResponse = await SignupService.signup(payload);
      const errorMessage = this.getErrorMessage(response);

      if (response.status !== 1) {
        this.signupFailed(errorMessage);
        return response.status === 2 ? null : errorMessage;
      }

      const { firstName, lastName, entityName } = SignupHelper.getNamesAndEntityFromEmail(
        payload.email
      );

      await this.signupSuccess({
        registrationUrl: response.registration_url,
        email: payload.email,
        emailScore: response.email_score,
        queryParams: {
          firstName,
          lastName,
          lang: payload.language,
          entityName,
          entityCountryCode: '',
        },
      });

      return null;
    } catch (e) {
      this.signupFailed();
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
      return this.getErrorMessage();
    }
  }

  static async signupSuccess({
    email,
    registrationUrl,
    emailScore,
    queryParams,
  }: {
    email: string;
    registrationUrl: string;
    emailScore?: number;
    queryParams: SignupQueryParams;
  }) {
    try {
      if (email) {
        await Hubspot.identify(email);
        await Hubspot.signup(email);
        Analytics.identify(undefined, {
          [AnalyticsUserTrait.Email]: email,
        });
      }
      if (!registrationUrl) {
        Analytics.track(AnalyticsEvent.SignupBadEmail, {
          [AnalyticsUserTrait.EmailScore]: emailScore,
        });
      }
    } catch (e) {
      if (bugsnagClient) {
        bugsnagClient.notify(e);
      }
    }

    let url = `${registrationUrl}&submit=true`;
    if (!registrationUrl) {
      url = '/login/verify-email';
      LocalStorageHelper.setLocalStorage(LocalStorageKey.VerificationEmail, email);
    }

    const redirectUrlWithParams = this.addQueryParamsToUrl({
      url,
      queryParams,
    });

    gtag('event', 'generate_lead', {
      page_title: 'Signup',
      page_path: '/signup',
      lead_type: 'free_account',
      page_referrer: document.referrer,
    });

    window.location.href = redirectUrlWithParams;
  }

  static addQueryParamsToUrl({
    url,
    queryParams,
  }: {
    url: string;
    queryParams: SignupQueryParams;
  }) {
    const urlParams: URLSearchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(queryParams)) {
      urlParams.append(key as string, value);
    }
    return `${url}?${urlParams.toString()}`;
  }

  static signupFailed(errorMessage: ErrorMessage = unknownError) {
    Analytics.track(AnalyticsEvent.SignupFailed, {
      [AnalyticsUserTrait.SignupStep]: 1,
      errorMessage: errorMessage.title,
    });
    Analytics.identify(undefined, {
      [AnalyticsUserTrait.SignupStep]: 1,
    });
  }

  static getErrorMessage(response?: SignupResponse) {
    let errorMessage: ErrorMessage;
    switch (response?.status) {
      case 3:
        errorMessage = {
          title: 'invalid-formatted-email',
          description: 'did-you-mean_v2',
          variables: {
            didYouMean: response.did_you_mean,
          },
        };
        break;
      case 4:
        errorMessage = {
          title: 'email-address-looks-wrong',
          description: 'did-you-mean_v2',
          variables: {
            didYouMean: response.did_you_mean,
          },
        };
        break;
      case 0:
        if (response.error === 'Could not create user') {
          errorMessage = {
            title: 'could-not-create-user',
            description: unknownError.description,
          };
          if (bugsnagClient) {
            bugsnagClient.notify(response.error);
          }
        } else if (response.error === 'something-went-wrong') {
          errorMessage = unknownError;
          if (bugsnagClient) {
            bugsnagClient.notify(response.error);
          }
        } else {
          errorMessage = {
            title: 'please-enter-your-business-email',
            description: 'private-or-anonymous-email-is-not-allowed',
          };
        }
        break;
      case 5:
        errorMessage = {
          title: 'you_have_reached_the_max_number_of_signups-title',
          description: 'you_have_reached_the_max_number_of_signups-description',
        };
        break;
      case 6:
        errorMessage = {
          title: 'you_have_entered_an_invalid_email-title',
          description: 'you_have_entered_an_invalid_email-description',
        };
        break;
      default:
        errorMessage = unknownError;
    }
    return errorMessage;
  }

  static launchIdentifier({
    identityProvider,
    templateId,
    registrationCampaignId,
    language,
  }: {
    identityProvider: IdentityProvider;
    templateId: string;
    registrationCampaignId: string;
    language: Language;
  }) {
    const redirUrl = IdentityProviderHelper.createIdentityProviderRedirUrl(
      identityProvider,
      'signup',
      registrationCampaignId,
      language,
      templateId
    );
    const requestUrl = IdentityProviderHelper.createIdentityProviderReqUrl(
      redirUrl,
      identityProvider
    );

    const popupProps: {
      height: number;
      width: number;
      top: number;
      left: number;
    } = IdentityProviderHelper.centerPopup(600, 600);

    const popupSize = `width=${popupProps.width},height=${popupProps.height},top=${popupProps.top},left=${popupProps.left}`;

    window.open(
      requestUrl,
      'authWindow',
      `toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes,${popupSize}`
    );
  }

  static validateIdentityProviderMessage({ event, url }: { event: MessageEvent; url: string }) {
    const { data }: { data: IdentityProviderResponseDTO } = event;
    const popupName = 'authWindow';

    if (data.status !== 1 || !Object.prototype.hasOwnProperty.call(data, 'result')) {
      this.closePopup({ name: popupName, url });
      const errorMessage = this.getErrorMessage(data);
      this.signupFailed(errorMessage);
      return;
    }

    this.closePopup({ name: popupName, url });
    this.signupSuccess({
      registrationUrl: data.result.registration_url,
      email: data.result?.user.email,
      queryParams: {
        firstName: data.result?.user.first_name,
        lastName: data.result?.user.last_name,
        lang: data.result?.user.language as Language,
        entityName: data.result?.entity.entity_name,
        entityCountryCode: data.result?.entity.entity_country_code,
      },
    });
  }

  static closePopup({ name, url }: { name: string; url: string }) {
    const popup: Window = window.open('', name);
    if (!popup) {
      return;
    }
    popup.postMessage({ [messageValidator]: '' }, url);
  }
}
